import type { SparseTimeSeries, TimeSeriesV2 } from './backendTypes';

export type TimeSeriesValue = number | null;

/** Common read API for map-backed and future dense time series implementations. */
export interface TimeSeries {
    readonly startDate: string;
    readonly intervalDays: number;
    readonly length: number;

    getValueAtDate(date: string): TimeSeriesValue;
    getDateRange(): { from: string; to: string };
    keys(): IterableIterator<string>;
    values(): IterableIterator<TimeSeriesValue>;
    entries(): IterableIterator<[string, TimeSeriesValue]>;
    [Symbol.iterator](): IterableIterator<[string, TimeSeriesValue]>;
    toMap(): SparseTimeSeries;
    inner(): TimeSeriesV2;
}

const MS_PER_DAY = 86_400_000;

/**
 * Anchor date for zero-length series. An empty series has no real start date, but
 * `startDate` must stay a valid ISO date so stored/transferred series always pass
 * `TimeSeriesV2Runtype` validation.
 */
export const EMPTY_SERIES_START_DATE = '1970-01-01';

/** Adds `days` to an ISO `YYYY-MM-DD` date, returning a new `YYYY-MM-DD` string. */
export function isoAddDays(isoDate: string, days: number): string {
    const date = new Date(`${isoDate}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}

/** Whole-day distance from `from` to `to` (both `YYYY-MM-DD`); negative if `to` precedes `from`. */
export function isoDiffDays(from: string, to: string): number {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY);
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True for strict `YYYY-MM-DD` strings that parse to a real calendar date. */
export function isValidIsoDate(value: string): boolean {
    return ISO_DATE_RE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
}

/** Structural guard for the dense `TimeSeriesV2` representation (vs e.g. a sparse `Map`). */
export function isDenseTimeSeries(value: unknown): value is TimeSeriesV2 {
    return (
        typeof value === 'object' &&
        value !== null &&
        !(value instanceof Map) &&
        'startDate' in value &&
        'intervalDays' in value &&
        Array.isArray((value as { values?: unknown }).values)
    );
}

/**
 * Materializes a sparse date→value map onto a dense series with uniform `intervalDays` spacing.
 * Gaps become `null`. Empty maps yield a zero-length series anchored at
 * `EMPTY_SERIES_START_DATE`.
 *
 * Off-grid dates snap to the grid point at-or-before them (`Math.floor`), matching the
 * last-observation-carried-forward lookup in `getValueAtDate` so a value written at a date is
 * readable at that same date.
 */
export function mapToTimeSeriesV2(values: SparseTimeSeries, intervalDays = 7): TimeSeriesV2 {
    const keys = Array.from(values.keys()).filter(isValidIsoDate).sort();
    const startDate = keys.at(0) ?? EMPTY_SERIES_START_DATE;
    const lastDate = keys.at(-1) ?? startDate;
    const length = keys.length === 0 ? 0 : Math.floor(isoDiffDays(startDate, lastDate) / intervalDays) + 1;

    const dense: TimeSeriesValue[] = new Array(length).fill(null);
    for (const [date, value] of values) {
        if (!isValidIsoDate(date)) continue;
        const index = Math.floor(isoDiffDays(startDate, date) / intervalDays);
        if (index >= 0 && index < length) {
            dense[index] = value;
        }
    }
    return { startDate, intervalDays, values: dense, issues: new Array<number>(length).fill(0) };
}

/**
 * `TimeSeries` implementation backed directly by the dense `TimeSeriesV2`
 * representation. Reads are O(1) index arithmetic; no map is materialized, and
 * the `issues` mask is retained on the wrapped series for later use.
 */
class DenseTimeSeries implements TimeSeries {
    readonly startDate: string;
    readonly intervalDays: number;
    readonly length: number;

    private readonly series: TimeSeriesV2;

    constructor(series: TimeSeriesV2) {
        this.series = series;
        this.startDate = series.startDate;
        this.intervalDays = series.intervalDays;
        this.length = series.values.length;
    }

    private dateAt(index: number): string {
        return isoAddDays(this.startDate, index * this.intervalDays);
    }

    /**
     * Returns the value at the grid point at-or-before `date` (last-observation-carried-forward).
     * Dates before `startDate` return null; off-grid dates never snap forward to a future point,
     * matching the closest-previous fallback the chart call sites apply on a null result.
     */
    getValueAtDate(date: string): TimeSeriesValue {
        const index = Math.floor(isoDiffDays(this.startDate, date) / this.intervalDays);
        if (index < 0 || index >= this.length) return null;
        return this.series.values[index] ?? null;
    }

    getDateRange(): { from: string; to: string } {
        return { from: this.startDate, to: this.length === 0 ? this.startDate : this.dateAt(this.length - 1) };
    }

    *keys(): IterableIterator<string> {
        for (let index = 0; index < this.length; index++) {
            yield this.dateAt(index);
        }
    }

    *values(): IterableIterator<TimeSeriesValue> {
        for (const value of this.series.values) {
            yield value ?? null;
        }
    }

    *entries(): IterableIterator<[string, TimeSeriesValue]> {
        for (let index = 0; index < this.length; index++) {
            yield [this.dateAt(index), this.series.values[index] ?? null];
        }
    }

    [Symbol.iterator](): IterableIterator<[string, TimeSeriesValue]> {
        return this.entries();
    }

    toMap(): SparseTimeSeries {
        const map: SparseTimeSeries = new Map();
        for (let index = 0; index < this.length; index++) {
            map.set(this.dateAt(index), this.series.values[index] ?? null);
        }
        return map;
    }

    inner(): TimeSeriesV2 {
        return this.series;
    }
}

/** Wraps the dense wire/storage representation in a read-only `TimeSeries`. */
export function timeSeriesFromV2(series: TimeSeriesV2): TimeSeries {
    return new DenseTimeSeries(series);
}

/** A zero-length `TimeSeries`, for "no data yet" placeholders in the UI. */
export function emptyTimeSeries(intervalDays = 7): TimeSeries {
    return new DenseTimeSeries({ startDate: EMPTY_SERIES_START_DATE, intervalDays, values: [], issues: [] });
}
